import React, { useRef } from 'react';
import { array, bool, func, object } from 'prop-types';
import { compose } from 'ramda';
import SubscriptionPlan from '../subscription/SubscriptionPlan';
import { useTranslate, translateArray } from '../../translate/I18n';
import { withRouter } from '../../utils/router';
import { commonRoutes } from '../../routes';
import { connect } from 'react-redux';
import useAsyncLoad from '../../hooks/useAsyncLoad';
import LoadingLayer from '../elements/LoadingLayer';
import { cdnize, formatLanguage } from '../../utils/pureUtils';
import Box from '../atoms/Box';
import Button from '../atoms/Button';
import Card from '../atoms/Card';
import Img from '../atoms/Img';
import Link from '../atoms/Link';
import Text from '../atoms/Text';
import ContentCH from '../territory/ContentCH';
import TerritoryContent from '../territory/TerritoryContent';

import { scrollTo } from '../subscription/utils';
import { isInBrowser } from '../../utils/reactOrNext';

import PricingGreenWave from '../atoms/illustrations/PricingGreenWave';
import PricingWhiteWave from '../atoms/illustrations/PricingWhiteWave';

import CheckCircle from '../atoms/icons/CheckCircle';
import { ROLES } from '../../constants';

const {
  general: { register }
} = commonRoutes;

const Pricing = ({ history, loadPlans, plans, isLoading, preloadedPlans = null }) => {
  const { t, i18n } = useTranslate(['subscription', 'pricing']);
  const translatedRegisterRoute = register.to;
  const registerRouteWithFreelancerRole = `${translatedRegisterRoute}?role=${ROLES.FREELANCE}`;

  useAsyncLoad(() => {
    if (preloadedPlans === null) {
      loadPlans();
    }
  }, [preloadedPlans]);

  const missionSlotRef = useRef(null);

  const scrollToMissionSlot = () => {
    if (isInBrowser()) {
      scrollTo(missionSlotRef);
    }
  };

  const availablePlans = (preloadedPlans || plans || []).filter(plan => plan.isDisplayed);

  if (isLoading) {
    return <LoadingLayer />;
  }
  return (
    <>
      <Box
        display={{ _: 'none', md: 'block' }}
        position="absolute"
        left="0"
        right="0"
        top="140px"
        overflow="hidden"
        height="400px"
        zIndex="0">
        <PricingGreenWave />
        <PricingWhiteWave />
      </Box>
      <Box px={{ _: 2, md: '5%' }} maxWidth="1280px" mx="auto">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexDirection={{ _: 'column', lg: 'row' }}
          mb={10}
          pt={5}>
          <Text tag="h1" mb={{ _: 3, lg: 0 }} styleName="wH2">
            {t('pricing:title')}
          </Text>
          <Card py={1} px={2} textAlign={{ _: 'center', lg: 'left' }}>
            {t('pricing:areYouFreelance')}{' '}
            <Link type="tertiary" withicon={1} to={registerRouteWithFreelancerRole} data-cy={`freelancer-signup-${i18n.language}`}>
              {t('pricing:createFreeAccount')}
            </Link>
          </Card>
        </Box>
        <SubscriptionPlan
          handleSetPlan={() => history.push(translatedRegisterRoute)}
          onclickPlan={scrollToMissionSlot}
          plans={availablePlans}
        />

        <Box
          display="flex"
          flexDirection={{ _: 'column', lg: 'row' }}
          alignItems={{ _: 'center', lg: 'start' }}
          justifyContent="space-around"
          mt={10}
          mb={14}>
          <Box width="100%" maxWidth="422px" mb={{ _: 6, lg: 0 }}>
            <Text styleName="wH3" mb={1}>
              {t('pricing:company:titlequestion')}
            </Text>
            <Text styleName="wH2" mb={2}>
              {t('pricing:company:title')}
            </Text>
            <ul>
              {translateArray(t, 'pricing:company:package').map((item, key) => {
                return (
                  <li key={key}>
                    <CheckCircle color="main" fontSize="12px" />
                    <Text tag="span" styleName="wPBig" ml={1}>
                      {item}
                    </Text>
                  </li>
                );
              })}
            </ul>
          </Box>
          <Box width="100%" maxWidth="422px">
            <Text styleName="wH3" mb={1}>
              {t('pricing:freelance:titlequestion')}
            </Text>
            <Text styleName="wH2" mb={2}>
              {t('pricing:freelance:title')}
            </Text>
            <ul>
              {translateArray(t, 'pricing:freelance:package').map((item, i) => {
                return (
                  <li key={i}>
                    <CheckCircle color="main" fontSize="12px" />
                    <Text tag="span" styleName="wPBig" ml={1}>
                      {item}
                    </Text>
                  </li>
                );
              })}
            </ul>
            <Box textAlign={{ _: 'center', lg: 'left' }}>
              <Button
                mt={3}
                styleType="secondary"
                shape="rounded"
                data-cy={`freelancer-signup-${i18n.language}-2`}>
                <Link type="quinary" to={registerRouteWithFreelancerRole}>
                  {t('pricing:signUpFreelance')}
                </Link>
              </Button>
            </Box>
          </Box>
        </Box>

        <Box
          ref={missionSlotRef}
          mb={20}
          display="flex"
          flexDirection={{ _: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems="center">
          <Box
            flex="1"
            px={4}
            py={5}
            border="1px solid"
            borderColor="greyVerylight"
            borderRadius={2}
            mb={{ _: 3, lg: 0 }}>
            <TerritoryContent
              render={() => (
                <Img
                  alt="Active missions illustration"
                  src={cdnize(`/pricing/active-missions-${formatLanguage(i18n.language)}.webp`)}
                  fallback={cdnize(`/pricing/active-missions-${formatLanguage(i18n.language)}.png`)}
                  srcRetina={cdnize(`/pricing/active-missions-${formatLanguage(i18n.language)}@2x.webp`)}
                  fallbackRetina={cdnize(`/pricing/active-missions-${formatLanguage(i18n.language)}@2x.png`)}
                  lazyloading={true}
                />
              )}
              onlyFor={['io', 'lu']}
            />

            <ContentCH
              render={() => (
                <Img
                  alt="Active missions illustration"
                  src={cdnize(`/pricing/active-missions-${formatLanguage(i18n.language)}-CH.webp`)}
                  fallback={cdnize(`/pricing/active-missions-${formatLanguage(i18n.language)}-CH.png`)}
                  srcRetina={cdnize(`/pricing/active-missions-${formatLanguage(i18n.language)}-CH@2x.webp`)}
                  fallbackRetina={cdnize(`/pricing/active-missions-${formatLanguage(i18n.language)}-CH@2x.png`)}
                  lazyloading={true}
                />
              )}
            />
          </Box>
          <Box flex="1" ml={{ _: 0, lg: 4 }}>
            <Text tag="h2" styleName="wH2" mb={3}>
              {t('pricing:activeMission.title')}
            </Text>
            <Text styleName="wPBig" mb={2}>
              {t('pricing:activeMission.main')}
            </Text>
            <Text styleName="wPBig">{t('pricing:activeMission.needMore')}</Text>
          </Box>
        </Box>
      </Box>
    </>
  );
};

Pricing.propTypes = {
  t: func,
  history: object,
  loadPlans: func,
  plans: array,
  isLoading: bool,
  preloadedPlans: array
};

export const NextJsPricing = compose(withRouter)(Pricing);

export default compose(
  withRouter,
  connect(
    ({ resubscription: { plans, isFetching } }) => ({ plans, isLoading: isFetching }),
    ({ resubscription: { loadPlans } }) => ({ loadPlans })
  )
)(Pricing);
